import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde'
import { MESSAGES } from 'pages/reporting/shared/constants'
import { BaseFormData } from 'pages/reporting/shared/types'
import { useCallback } from 'react'

interface UsePublishRevisionHandlerProps<TFormData extends BaseFormData> {
  metadataEntryId: string
  formData: TFormData
  currentRevisionNumber: number
  publishLatestDraftPDF: (pdfPath: string) => Promise<void>
  publishRevision: () => Promise<void> | Promise<string>
  setIsSubmitting: (value: boolean) => void
  generatePdfPath: (timestamp: string) => string
  // Optional callback to run before publishing (e.g., asset verification). Return false to abort
  beforePublish?: (dataToUse: TFormData) => Promise<boolean>
}

interface UsePublishRevisionHandlerResult<TFormData extends BaseFormData> {
  handlePublishRevision: (dataToUse?: TFormData) => Promise<void>
}

export const usePublishRevisionHandler = <TFormData extends BaseFormData>({
  metadataEntryId,
  formData,
  currentRevisionNumber,
  publishLatestDraftPDF,
  publishRevision,
  setIsSubmitting,
  generatePdfPath,
  beforePublish,
}: UsePublishRevisionHandlerProps<TFormData>): UsePublishRevisionHandlerResult<TFormData> => {
  const { rolodexPatch } = useRolodexEdit()

  const handlePublishRevision = useCallback(
    async (dataToUse: TFormData = formData) => {
      try {
        setIsSubmitting(true)

        message.loading({
          content: 'Verifying signatures...',
          key: 'publish',
          duration: 0,
        })

        if (!dataToUse.signoff?.supervisorSignoff) {
          throw new Error('Supervisor signature required before publishing')
        }
        if (!dataToUse.signoff?.reviewerSignoff) {
          throw new Error('Reviewer signature required before publishing')
        }

        // Run optional pre-publish hook (e.g., asset verification)
        if (beforePublish) {
          const shouldContinue = await beforePublish(dataToUse)
          if (!shouldContinue) {
            throw new Error('Pre-publish verification failed')
          }
        }

        message.loading({
          content: 'Publishing PDF...',
          key: 'publish',
          duration: 0,
        })

        const timestamp = dayjs().format('YYYYMMDD-HHmmss')
        const pdfPath = generatePdfPath(timestamp)

        await publishLatestDraftPDF(pdfPath)

        message.loading({
          content: 'Updating revision metadata...',
          key: 'publish',
          duration: 0,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              revisionMetadata: {
                revisionNumber: currentRevisionNumber,
                createdAt: dataToUse.revisionMetadata.createdAt,
                publishedAt: dayjs().toISOString(),
                isPublished: true,
              },
              published_pdf_path: pdfPath,
              lockState: {
                isLocked: true,
                startedSignoff: true,
                lastLockTimestamp: dayjs().toISOString(),
              },
            },
          },
        ])

        await publishRevision()

        message.success({
          content: MESSAGES.SUCCESS.PUBLISH,
          key: 'publish',
        })
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to publish revision'
        console.error('Failed to publish revision:', error)
        message.error({
          content: errorMessage,
          key: 'publish',
        })
        throw error
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      formData,
      beforePublish,
      generatePdfPath,
      currentRevisionNumber,
      publishLatestDraftPDF,
      rolodexPatch,
      metadataEntryId,
      publishRevision,
      setIsSubmitting,
    ]
  )

  return { handlePublishRevision }
}
